/* Atomz panel - the search page's filters, on a phone, as one button.
 *
 * THE PROBLEM: the collection page draws its own rail, so it got a Filter
 * button and a full-screen panel with sort at the top and one Apply at the
 * bottom. Search is the app's render, and there the same shopper still gets a
 * horizontal row of filter chips above the grid with the sort control beside
 * it: two controls, both in the way, neither of them the panel. Same shop,
 * same catalog, two different ways to narrow it.
 *
 * WHAT IT DOES: wraps the app's own filter container and its own sort control
 * in a theme-owned shell and puts a Filter button above the grid. Open, and
 * the shell becomes the panel; closed, the shell is `display: contents` and
 * the page is exactly what it was.
 *
 * WHAT IT DOES NOT DO: it never applies, removes or reorders a filter, and it
 * never builds a URL. Every control inside the panel is the app's own, in the
 * app's own container, with the app's own handlers still bound to it. Clear
 * all clicks the app's Clear All. That is also why the footer button says
 * Show results rather than Apply: the app refetches on each tick, so by the
 * time the shopper reaches the footer the results behind the panel are
 * already the ones it names, and the button only closes the panel. Promising
 * an Apply here would be describing a commit that had already happened.
 *
 * WHY MOVING THE NODES IS SAFE: a listener lives on the node, not on where
 * the node sits, and the app binds at the document or at the element. The
 * containers keep their identity, their classes and their children. Nothing
 * here writes inside them.
 *
 * FAILURE MODE: if the app renames its containers, nothing is found, no shell
 * is built and the page is the chip bar it was. The button is only shown once
 * the shell exists.
 */
(function () {
  'use strict';

  if (!/\/search/.test(window.location.pathname)) return;

  var MAX_WIDTH = 989;
  var DEBOUNCE = 60;

  function q(sel) { return document.querySelector(sel); }

  function els() {
    return {
      filters: q('.az-filters-container'),
      sort: q('.az-sort-by-container'),
      /*
       * The product grid, not the app block. `atomz-product-grid` IS
       * .az-app-block -- the crumb, the filters, the sort and the grid are all
       * its children -- so inserting before it put the Filter button above the
       * whole page, over the breadcrumb.
       */
      grid: q('#az-product-grid') || q('.az-product-grid'),
    };
  }

  var shell = null;
  var bar = null;
  var openBtn = null;
  var countEl = null;
  var lastFocus = null;

  function build() {
    if (shell) return true;
    var e = els();
    if (!e.filters || !e.grid || !e.filters.parentNode) return false;

    shell = document.createElement('div');
    shell.className = 'az-panel';
    e.filters.parentNode.insertBefore(shell, e.filters);

    var head = document.createElement('div');
    head.className = 'az-panel__head';
    var title = document.createElement('h2');
    title.className = 'az-panel__title';
    title.textContent = 'Filter';
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'az-panel__close';
    close.setAttribute('aria-label', 'Close filters');
    close.innerHTML = '&times;';
    close.addEventListener('click', hide);
    head.appendChild(title);
    head.appendChild(close);

    var body = document.createElement('div');
    body.className = 'az-panel__body';
    /*
     * Source order stays filters-then-sort, which is the order the page has
     * outside the panel and the order `display: contents` falls back to. The
     * panel puts sort first with `order`, which only applies while the body
     * is a flex container, which is only while the panel is open.
     */
    body.appendChild(e.filters);
    if (e.sort) body.appendChild(e.sort);

    var foot = document.createElement('div');
    foot.className = 'az-panel__foot';
    var clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'az-panel__clear';
    clear.textContent = 'Clear all';
    clear.addEventListener('click', function () {
      var own = q('.az-clear-filters');
      if (own) own.click();
    });
    var done = document.createElement('button');
    done.type = 'button';
    done.className = 'az-panel__go';
    done.textContent = 'Show results';
    done.addEventListener('click', hide);
    foot.appendChild(clear);
    foot.appendChild(done);

    shell.appendChild(head);
    shell.appendChild(body);
    shell.appendChild(foot);

    bar = document.createElement('div');
    bar.className = 'az-panelbar';
    openBtn = document.createElement('button');
    openBtn.type = 'button';
    openBtn.className = 'az-panelbar__btn';
    openBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">' +
      '<path d="M4 6h16M7 12h10M10 18h4" stroke-linecap="round"/>' +
      '<circle cx="14" cy="6" r="2" fill="var(--az-bg)"/>' +
      '<circle cx="10" cy="12" r="2" fill="var(--az-bg)"/>' +
      '<circle cx="14" cy="18" r="2" fill="var(--az-bg)"/></svg>' +
      '<span>Filter</span>';
    countEl = document.createElement('span');
    countEl.className = 'az-panelbar__n';
    countEl.hidden = true;
    openBtn.appendChild(countEl);
    openBtn.addEventListener('click', show);
    bar.appendChild(openBtn);
    e.grid.parentNode.insertBefore(bar, e.grid);
    bar.setAttribute('data-az-ready', '');

    watch();
    paint();
    return true;
  }

  /*
   * MOVED TO THE BODY TO OPEN, AND PUT BACK TO CLOSE.
   *
   * The panel is `position: fixed; inset: 0`, which should mean the viewport.
   * It did not. Dawn wraps every section in
   *
   *   <div class="scroll-trigger animate--slide-in">
   *
   * and that carries transform: matrix(1, 0, 0, 1, 0, 0) -- an identity
   * transform, which changes nothing visually and still makes the element a
   * containing block for any fixed descendant. So `inset: 0` resolved against
   * a wrapper 4864px tall instead of a 780px screen: the panel opened at y=263
   * running 4864px down, and everything past the first screenful, the Apply
   * bar at y=5058 included, was off the bottom with no way to scroll to it.
   * On a phone that reads as a filter sheet that will not take a tap.
   *
   * Reparenting to the body is the fix that does not care what the theme wraps
   * a section in. A placeholder marks the spot so close puts it back exactly,
   * which matters because the shell holds the app's own containers and above
   * 989px it goes back to `display: contents` inside the app block.
   *
   * Listeners live on nodes, not on where the nodes sit, so the app's handlers
   * come along untouched.
   */
  var slot = null;

  function show() {
    if (!shell) return;
    lastFocus = openBtn;

    if (shell.parentNode !== document.body) {
      if (!slot) slot = document.createComment('az-panel');
      shell.parentNode.insertBefore(slot, shell);
      document.body.appendChild(shell);
    }

    shell.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    var first = shell.querySelector('.az-panel__close');
    if (first) first.focus();
  }

  function hide() {
    if (!shell) return;
    shell.classList.remove('is-open');
    document.body.style.overflow = '';

    if (slot && slot.parentNode) {
      slot.parentNode.insertBefore(shell, slot);
      slot.parentNode.removeChild(slot);
      slot = null;
    }

    if (lastFocus) lastFocus.focus();
  }

  /*
   * How many values are on. Counted the same way atomz-applied.js counts them,
   * off the app's own selected rows, with the category groups left out: the
   * breadcrumb owns category, so counting it here would number the place the
   * shopper is standing as though they had filtered to it.
   */
  function activeCount() {
    var rows = document.querySelectorAll('.az-filter .az-filter-item-value.selected');
    var n = 0;
    for (var i = 0; i < rows.length; i++) {
      var group = rows[i].closest('.az-filter');
      var id = group && group.querySelector('.az-filter-button');
      if (id && id.id && /categor/i.test(id.id)) continue;
      n++;
    }
    return n;
  }

  function paint() {
    if (!countEl) return;
    var n = activeCount();
    countEl.textContent = n ? String(n) : '';
    countEl.hidden = n === 0;
  }

  /* Selection is a class on a row and changes on a plain click with no event
     of its own, so the DOM is what gets watched -- the shopper's tick, a clear
     all, and the re-render after a new search all land the same way. */
  var timer = null;
  function watch() {
    var host = q('.az-filter-wrapper') || q('.az-filters-container');
    if (!host) return;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(paint, DEBOUNCE);
    }).observe(host, { childList: true, subtree: true, attributes: true,
                       attributeFilter: ['class'] });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && shell && shell.classList.contains('is-open')) hide();
  });

  /* Above 989 the shell is `display: contents` and the panel rules do not
     apply, so nothing has to be torn down -- but an open panel left over from
     a rotation would keep the body locked. */
  window.addEventListener('resize', function () {
    if (window.innerWidth > MAX_WIDTH) hide();
  });

  /*
   * The app renders the filter container after its first response, so there is
   * nothing to wrap at DOMContentLoaded. Polled rather than hooked to a search
   * event: the event fires when the data arrives, not when the container is in
   * the DOM, and a build that runs one frame early silently produces no button.
   */
  var tries = 0;
  (function attempt() {
    if (build()) return;
    if (++tries > 100) return;
    setTimeout(attempt, 100);
  })();
})();
